import { createSelector } from '@reduxjs/toolkit';

import FilterData from 'components/features/Shuffler/data/filters';

import { notNull, validCustomSize } from 'utils/type_guards';
import { byId } from 'utils/common';

import canvases from 'domains/canvases';
import catalogs from 'domains/catalogs';
import subcatalogs from 'domains/subcatalogs';
import products from 'domains/products';
import optionGroups from 'domains/option_groups';
import svgs from 'domains/svgs';

import type { RootState } from 'store/wos_store';
import type { DisabledOption } from 'domains/option_groups';
import type { NodeAPIData } from 'types/catalog_types';

const selectActiveCanvasId = (state: RootState) => state.canvases.active;

const selectActiveCanvas = (state: RootState) => {
  const id = selectActiveCanvasId(state);
  if (!id) return;

  return canvases.selectors.selectById(state, id);
};

/*
 * Active Catalog Data
 */
const selectActiveCatalogId = (state: RootState) => selectActiveCanvas(state)?.catalog;

const selectActiveCatalog = (state: RootState) => {
  const id = selectActiveCatalogId(state);
  if (!id) return;

  return catalogs.selectors.selectById(state, id);
};

// if the activeCatalog is in FilterData then catalog has Advanced Filters (shuffler)
const selectHasAdvancedFilters = createSelector(
  [selectActiveCatalog],
  (activeCatalog) => !!activeCatalog && Object.keys(FilterData).indexOf(activeCatalog.name) > -1
);

const selectSubcatalogsForActiveCatalog = (state: RootState) => {
  const catalogId = selectActiveCatalogId(state);
  if (!catalogId) return [];

  return subcatalogs.selectors.allByCatalogId(state)[catalogId] || [];
};

/*
 * Active Subcatalog Data
 */
const selectActiveSubcatalogId = (state: RootState) => selectActiveCanvas(state)?.subcatalog;

const selectActiveSubcatalog = (state: RootState) => {
  const id = selectActiveSubcatalogId(state);
  if (!id) return;

  return subcatalogs.selectors.selectById(state, id);
};

const selectActiveSubcatalogFilters = (state: RootState) => {
  const subcatalog = selectActiveSubcatalog(state);
  if (!subcatalog) return [];

  return subcatalog.filter_default.trim().split(',');
};

const selectProductsForActiveSubcatalog = createSelector(
  [selectActiveSubcatalog, products.selectors.selectEntities],
  (subcatalog, allProductsById) => {
    if (!subcatalog) return [];
    return subcatalog.templates.map((id) => allProductsById[id]).filter(notNull);
  }
);

const selectFilteredProductsForActiveSubcatalog = createSelector(
  [products.selectors.appliedFilter, selectProductsForActiveSubcatalog],
  (filter, productsBySubcatalog) => {
    if (!filter || filter === '') {
      return productsBySubcatalog;
    }

    return productsBySubcatalog.filter((product) => {
      if (filter === 'All') {
        return !products.utils.isCustomSizeProduct(product);
      }

      return products.utils.getProductFilters(product).indexOf(filter) > -1;
    });
  }
);

/*
 * Active Product Data
 */
const selectActiveProductId = (state: RootState) => selectActiveCanvas(state)?.product;

const selectActiveProduct = (state: RootState) => {
  const id = selectActiveProductId(state);
  if (!id) return;

  return products.selectors.selectById(state, id);
};

const selectActiveProductIsCustomProduct = createSelector(
  [selectActiveProduct],
  (activeProduct) => activeProduct && products.utils.isCustomSizeProduct(activeProduct)
);

const selectOptionGroupsForActiveProduct = createSelector(
  [
    selectActiveProductId,
    optionGroups.selectors.selectEntities,
    optionGroups.selectors.allByTemplateId,
  ],
  (activeProductId, optionGroupsById, optionGroupsByTemplateId) => {
    if (!activeProductId) return [];

    const optionGroupIds = optionGroupsByTemplateId[activeProductId] || [];
    return optionGroupIds.map((id) => optionGroupsById[id]).filter(notNull);
  }
);

/*
 * Active Options Data
 */
const selectActiveCanvasOptionConfig = createSelector([selectActiveCanvas], (activeCanvas) => {
  return activeCanvas && activeCanvas.options ? activeCanvas.options : {};
});

const selectActiveOptionGroupFilters = createSelector(
  [selectOptionGroupsForActiveProduct],
  (activeOptionGroups) => {
    return activeOptionGroups.reduce((acc: Record<number, string[]>, optionGroup) => {
      const filters = optionGroup.filter_default.trim().split(',');
      if (optionGroup.filter_default.length > 0) {
        acc[optionGroup.id] = filters;
      }

      return acc;
    }, {});
  }
);

const selectDisabledOptions = createSelector(
  [optionGroups.selectors.selectEntities, selectActiveCanvasOptionConfig],
  (allOptionGroupsById, activeOptions) => {
    const allDisableIds = Object.entries(activeOptions).reduce(
      (acc: Record<string, DisabledOption>, [optionGroupId, optionId]) => {
        const optionGroup = allOptionGroupsById[optionGroupId];
        if (!optionGroup) return acc;

        const option = optionGroup.options.find(byId(optionId));
        if (!option || option.disable_ids.length === 0) return acc;

        option.disable_ids.split(',').forEach((disableId) => {
          acc[disableId] = { id: disableId, by: option.name_long };
        });

        return acc;
      },
      {}
    );

    return allDisableIds;
  }
);

const selectActiveOptions = createSelector(
  [optionGroups.selectors.selectEntities, selectActiveCanvasOptionConfig],
  (allOptionGroupsById, activeOptionConfig) => {
    if (activeOptionConfig) {
      return Object.entries(activeOptionConfig)
        .map(([optionGroupId, optionId]) =>
          allOptionGroupsById[optionGroupId]?.options.find(byId(optionId))
        )
        .filter(notNull);
    }

    return [];
  }
);

/*
 * Active Custom Size Data
 */
const selectActiveCanvasCustomSizes = createSelector([selectActiveCanvas], (activeCanvas) => {
  if (!activeCanvas || !validCustomSize(activeCanvas)) return { height: 0, width: 0 };

  return { height: activeCanvas.height, width: activeCanvas.width };
});

const selectActiveCanvasCustomPrice = createSelector([selectActiveCanvas], (activeCanvas) =>
  activeCanvas && activeCanvas.customPrice ? activeCanvas.customPrice : '0'
);

/**
 * Active Nodes
 */
const selectActiveProductNodes = createSelector([selectActiveProduct], (activeProduct) => {
  return activeProduct ? activeProduct.nodes : [];
});

const selectActiveOptionNodes = createSelector([selectActiveOptions], (activeOptions) => {
  return activeOptions.length > 0 ? activeOptions.flatMap((option) => option.nodes) : [];
});

const selectAllActiveNodes = createSelector(
  [selectActiveProductNodes, selectActiveOptionNodes],
  (productNodes, optionNodes) => {
    return [...productNodes, ...optionNodes];
  }
);

/*
 * Other Active Canvas Data
 */

const selectHasBorderColor = createSelector(
  [selectActiveProductNodes, selectActiveOptionNodes, svgs.selectors.selectEntities],
  (productNodes, optionNodes, svgsById) => {
    const merge: Record<string, boolean> = {};

    const checkHasBorderColor = (node: NodeAPIData) => {
      if (!node.svg_id) return false;

      const svg = svgsById[node.svg_id];
      merge[node.uid] = !!svg && !!svg.hasBorderColor;
    };

    // Check product nodes first, then override with option nodes
    productNodes.forEach(checkHasBorderColor);
    optionNodes.forEach(checkHasBorderColor);

    return Object.values(merge).some((hasColor) => hasColor);
  }
);

const selectActiveCanvasBorderColor = createSelector(
  [selectActiveCanvas],
  (activeCanvas) => activeCanvas?.borderColor
);

const selectEditingCartProductId = createSelector([selectActiveCanvas], (activeCanvas) =>
  activeCanvas ? activeCanvas.cartProductId : null
);

const selectIsCartProduct = (state: RootState) => {
  const canvas = selectActiveCanvas(state);

  return !!canvas && !!canvas.cartProductId;
};

const selectCanvasHasRotatedNodes = createSelector([selectActiveProduct], (product) => {
  if (product && product.nodes && product.nodes.length > 0) {
    const imageAndTextNodes = product.nodes.filter((n) => n.node_type === 1 || n.node_type === 3);

    return imageAndTextNodes.some((n) => !!n.rotation);
  }

  return false;
});

export const selectCanRotateActiveCanvas = (state: RootState) => {
  const product = selectActiveProduct(state);
  if (!product) return false;

  return products.utils.canRotateProduct(product);
};

export default {
  canvasId: selectActiveCanvasId,
  canvas: selectActiveCanvas,
  catalogId: selectActiveCatalogId,
  catalog: selectActiveCatalog,
  hasAdvancedFilters: selectHasAdvancedFilters,
  subcatalogs: selectSubcatalogsForActiveCatalog,
  subcatalogId: selectActiveSubcatalogId,
  subcatalog: selectActiveSubcatalog,
  subcatalogFilters: selectActiveSubcatalogFilters,
  products: selectProductsForActiveSubcatalog,
  filteredProducts: selectFilteredProductsForActiveSubcatalog,
  productId: selectActiveProductId,
  product: selectActiveProduct,
  isCustomProduct: selectActiveProductIsCustomProduct,
  optionGroups: selectOptionGroupsForActiveProduct,
  optionGroupFilters: selectActiveOptionGroupFilters,
  optionConfig: selectActiveCanvasOptionConfig,
  disabledOptions: selectDisabledOptions,
  selectedOptions: selectActiveOptions,
  customSize: selectActiveCanvasCustomSizes,
  customPrice: selectActiveCanvasCustomPrice,
  nodes: selectAllActiveNodes,
  hasBorderColor: selectHasBorderColor,
  borderColor: selectActiveCanvasBorderColor,
  cartProductId: selectEditingCartProductId,
  isCartProduct: selectIsCartProduct,
  hasRotatedNodes: selectCanvasHasRotatedNodes,
  canRotate: selectCanRotateActiveCanvas,
};
